import { Flex, Typography } from 'antd';
import {
  CaseTaskNameMap,
  EvidenceSectionName,
  EvidenceSectionNameMap,
  EvidenceTask,
  EvidenceTaskStatus,
  EvidenceTaskType,
} from 'api/brokerCases/types';
import { CloseOutlined, InfoCircleOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { getEvidenceTaskListPrimaryLabel, getEvidenceTaskTooltipDescription } from 'pages/EvidencePage/evidenceConfig';
import { Tooltip } from 'antd';
import { ApplicantOption, getApplicantDisplayNameForId } from 'pages/EvidencePage/helpers';
import { useIntl } from 'react-intl';
import { TypedFormatMessageFn } from 'types/intl';
import { useMemo } from 'react';

export interface IEvidenceItemActionListItemLabelProps {
  sectionName: EvidenceSectionName;
  task: EvidenceTask;
  applicants: ApplicantOption[];
  showTooltip: boolean;
  hasFailedReview: boolean;
  isUnderReview: boolean;
}

export const EvidenceItemActionListItemLabel = ({
  sectionName,
  task,
  applicants,
  showTooltip,
  hasFailedReview,
  isUnderReview,
}: IEvidenceItemActionListItemLabelProps) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const label = getEvidenceTaskListPrimaryLabel(task, sectionName, formatMessage);

  const variant = task.payload.taskType;
  const isCompleted = task.status === EvidenceTaskStatus.COMPLETED && task.name !== CaseTaskNameMap.OTHER_DOCUMENTS;

  const uploadFileStatus = useMemo((): {
    messageId:
      | 'evidence.task.status.completed'
      | 'evidence.task.status.notUploaded'
      | 'evidence.task.status.failedReview'
      | 'evidence.task.status.underReview';
    icon: 'success' | 'error' | 'review' | null;
    textColorClassName: string;
  } => {
    // we don't want to show it as failed if they have then completed the resulting task that caused
    if (hasFailedReview && !isCompleted) {
      return {
        messageId: 'evidence.task.status.failedReview',
        icon: 'error',
        textColorClassName: 'text-red-600',
      };
    }
    if (isUnderReview) {
      return {
        messageId: 'evidence.task.status.underReview',
        icon: 'review',
        textColorClassName: 'text-gray-500',
      };
    }
    if (isCompleted) {
      return {
        messageId: 'evidence.task.status.completed',
        icon: 'success',
        textColorClassName: 'text-green-600',
      };
    }
    return {
      messageId: 'evidence.task.status.notUploaded',
      icon: null,
      textColorClassName: 'text-gray-500',
    };
  }, [hasFailedReview, isUnderReview, isCompleted]);

  const showFurtherInfoIndividualBadge =
    sectionName === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED && task.payload.individualId;
  const individualBadgeName =
    showFurtherInfoIndividualBadge && task.payload.individualId
      ? getApplicantDisplayNameForId(applicants, task.payload.individualId)
      : null;

  return (
    <Flex vertical>
      <Flex align='center' className='min-w-0 flex-wrap gap-2'>
        <Typography.Text className='text-sm font-normal'>{label}</Typography.Text>
        {showFurtherInfoIndividualBadge && (
          <span className='inline-flex max-w-[min(100%,14rem)] shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5'>
            <UserOutlined className='shrink-0 text-[11px] leading-none text-gray-500' />
            <span className='truncate text-xs font-normal leading-4 text-gray-600'>{individualBadgeName}</span>
          </span>
        )}
        {showTooltip && (
          <Tooltip title={getEvidenceTaskTooltipDescription(task, sectionName, formatMessage)}>
            <InfoCircleOutlined className='text-xs leading-none text-gray-500' />
          </Tooltip>
        )}
      </Flex>
      {variant === EvidenceTaskType.UPLOAD_FILE ? (
        <Flex align='center' className='gap-1'>
          {uploadFileStatus.icon === 'success' && (
            <div className='flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-green-500 text-xs font-bold leading-none text-white'>
              ✓
            </div>
          )}
          {uploadFileStatus.icon === 'error' && (
            <div className='flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-bold leading-none text-white'>
              <CloseOutlined className='text-[8px]' />
            </div>
          )}
          {uploadFileStatus.icon === 'review' && (
            <div className='flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-gray-400 text-xs font-bold leading-none text-white'>
              <SearchOutlined className='text-[8px]' />
            </div>
          )}
          <Typography.Text className={`text-xs font-normal leading-5 ${uploadFileStatus.textColorClassName}`}>
            {formatMessage({ id: uploadFileStatus.messageId })}
          </Typography.Text>
        </Flex>
      ) : isCompleted ? (
        isUnderReview && !hasFailedReview ? (
          <Flex align='center' className='gap-1'>
            <div className='flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-gray-400 text-xs font-bold leading-none text-white'>
              <SearchOutlined className='text-[8px]' />
            </div>
            <Typography.Text className='text-xs font-normal leading-5 text-gray-500'>
              {formatMessage({ id: 'evidence.task.status.underReview' })}
            </Typography.Text>
          </Flex>
        ) : (
          <Flex align='center' className='gap-1'>
            <div className='flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-green-500 text-xs font-bold leading-none text-white'>
              ✓
            </div>
            <Typography.Text className='text-xs font-normal leading-5 text-green-600'>
              {formatMessage({ id: 'evidence.task.status.completed' })}
            </Typography.Text>
          </Flex>
        )
      ) : (
        <Typography.Text className='text-xs font-normal leading-5 text-gray-500'>
          {formatMessage({ id: 'evidence.task.status.notCompleted' })}
        </Typography.Text>
      )}
    </Flex>
  );
};
